import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { idListParam, nullableDate, nullableText, optionalDate, optionalText, userIdList } from '../common.js';
import { diffDays } from './dates.js';
/**
 * Verträge der Aufgaben im Modul „Termine und Aufgaben“ (KON-033, KON-051). Alles, was Aufgaben betrifft und von API und
 * Backoffice gemeinsam gebraucht wird, steht in diesem Ordner.
 */
export const TASK_STATUSES = ['open', 'done'];
export const TASK_STATUS_LABELS = { open: 'Offen', done: 'Erledigt' };
/**
 * Gliederung der Liste. „open“ sind alle offenen Aufgaben, die vier folgenden teilen sie nach Fälligkeit, „done“ sind die
 * erledigten. „Heute“ gilt in der Zeitzone Europe/Berlin und wird vom Server bestimmt, nicht vom Browser.
 */
export const TASK_GROUPS = ['open', 'overdue', 'today', 'later', 'nodate', 'done'];
export const TASK_GROUP_LABELS = {
    open: 'Offen',
    overdue: 'Überfällig',
    today: 'Heute',
    later: 'Später',
    nodate: 'Ohne Datum',
    done: 'Erledigt',
};
const title = z.string().trim().min(2, 'Titel fehlt').max(200);
/** Uhrzeit HH:MM (24 Stunden), leer = keine Uhrzeit */
const time = z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v === null || /^([01]\d|2[0-3]):[0-5]\d$/.test(v), 'Bitte eine Uhrzeit wie 08:30 angeben');
const optionalTime = time.optional().transform((v) => v ?? undefined);
const nullableTime = time.nullable().optional();
/** Beteiligte Mitarbeiter: die vollständige Liste, sie ersetzt die bisherige */
const participants = userIdList;
export const createTaskInput = z.object({
    title,
    description: optionalText(5000),
    /** Fälligkeitsdatum als JJJJ-MM-TT */
    dueDate: optionalDate,
    /** Zeitraum im Kalender (KON-087): Startdatum und Enddatum als JJJJ-MM-TT, beide freiwillig */
    startDate: optionalDate,
    endDate: optionalDate,
    /** Uhrzeiten zu Start- und Enddatum (HH:MM), freiwillig */
    startTime: optionalTime,
    endTime: optionalTime,
    /** Aufgabentyp = eine Terminart des Mandanten (Farbe im Kalender) */
    typeId: z.uuid('Ungültiger Aufgabentyp').optional(),
    /** Zuständig ist ein aktiver Benutzer desselben Mandanten */
    assignedUserId: z.uuid('Ungültiger Benutzer').optional(),
    /** Beteiligte Mitarbeiter, sie sehen die Aufgabe, bearbeiten dürfen nur die zuständige Person und das Büro */
    participantUserIds: participants.optional(),
});
export const updateTaskInput = z
    .object({
    title: title.optional(),
    description: nullableText(5000),
    dueDate: nullableDate,
    startDate: nullableDate,
    endDate: nullableDate,
    startTime: nullableTime,
    endTime: nullableTime,
    typeId: z.uuid('Ungültiger Aufgabentyp').nullable().optional(),
    assignedUserId: z.uuid('Ungültiger Benutzer').nullable().optional(),
    status: z.enum(TASK_STATUSES).optional(),
    participantUserIds: participants.optional(),
})
    // Streng, damit ein Aufrufer, der noch „report“ mitschickt, eine Fehlermeldung bekommt statt eines stillen Verwerfens
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/**
 * Report schreiben und Aufgabe erledigen. Den Report schreibt nur die zuständige Person dieser Aufgabe, sonst niemand, auch nicht das
 * Büro oder der Inhaber. Erledigen darf sie auch ohne das Recht zum Bearbeiten, ebenso das Büro. Weitere Felder lehnt die API ab
 * (strict), damit niemand über diesen Weg Titel oder Zuständigkeit ändert.
 */
export const taskProgressInput = z
    .object({
    report: nullableText(10000),
    status: z.enum(TASK_STATUSES).optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listTasksQuery = paginationQuery.extend({
    group: z.enum(TASK_GROUPS).optional(),
    assignedUserId: z.uuid().optional(),
    /** Aufgaben, bei denen der Benutzer zuständig oder beteiligt ist („Meine Aufgaben“) */
    involvedUserId: z.uuid().optional(),
    /** Aufgaben, bei denen der Benutzer als Beteiligter eingetragen ist (ohne die, in denen er nur zuständig ist) */
    participantUserId: z.uuid().optional(),
    q: z.string().trim().max(100).optional(),
});
/**
 * Aufgaben im Kalender (KON-087). Der Zeitraum einer Aufgabe ist Start bis Ende. Ist nur eines von beiden angegeben, gilt dieser Tag, fehlen beide,
 * gilt der Fälligkeitstag. Ohne jedes Datum erscheint die Aufgabe nicht im Kalender.
 */
export function taskSpan(t) {
    const start = t.startDate ?? t.endDate ?? t.dueDate;
    const end = t.endDate ?? t.startDate ?? t.dueDate;
    return start && end ? { start, end } : null;
}
export const listCalendarTasksQuery = z
    .object({
    from: z.iso.date('Ungültiges Datum'),
    to: z.iso.date('Ungültiges Datum'),
    /** Aufgaben, bei denen der Benutzer zuständig oder beteiligt ist */
    involvedUserId: z.uuid().optional(),
    /** Mehrere Personen zugleich, durch Kommas getrennt: zuständig oder beteiligt ist mindestens eine */
    involvedUserIds: idListParam,
    typeId: z.uuid().optional(),
    typeIds: idListParam,
    q: z.string().trim().max(100).optional(),
})
    .superRefine((v, ctx) => {
    if (v.to < v.from)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Das Ende darf nicht vor dem Beginn liegen.' });
    else if (diffDays(v.from, v.to) >= 100)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Höchstens 100 Tage auf einmal.' });
});
export const taskSummaryQuery = z.object({ assignedUserId: z.uuid().optional(), involvedUserId: z.uuid().optional(), participantUserId: z.uuid().optional() });
//# sourceMappingURL=tasks.js.map